import { useConfirm, useLoading } from '@genial/components';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import { tagsState } from '../../../store/batchTicket/atom';
import { errorMessageBatchTicketRequestItem } from '../../BatchTicketRequests/utils';
import { confirmItemTagWarningData } from '../utils';
import { debounce } from '../utils/tags';
import { CustomerItemTagObject } from '../utils/types';

import { getValidationCreateTagSchema, tagSchemaData } from './constants';

interface useCustomerTagContentProps {
  itemId: number;
}

export const useCustomerTagContent = ({ itemId }: useCustomerTagContentProps) => {
  const tags = useRecoilValue(tagsState);
  const { confirm } = useConfirm();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const confirmModalProps = {
    isMobile,
    ...confirmItemTagWarningData,
  };

  const { addAndLinkTag, removeTag, updateTagLabel, linkUnlinkTag } = useBatchTicketActions();

  const {
    control,
    formState: { isValid: createNewTagIsValid, errors },
    handleSubmit,
    setValue,
  } = useForm<tagSchemaData>({
    mode: 'all',
    resolver: zodResolver(getValidationCreateTagSchema(tags)),
    defaultValues: {
      tagValue: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'add' | 'edit'>('add');
  const [itemEdit, setItemEdit] = useState({} as CustomerItemTagObject);
  const { hide, show } = useLoading();

  const inputTextValue = mode === 'add' ? 'CRIAR' : 'SALVAR';
  const inputButtonAddIsDisabled = !createNewTagIsValid || !!errors.tagValue;
  const inputTextColor = inputButtonAddIsDisabled ? 'neutral.light' : 'brand.tertiary.principal';

  const { showToast, showToastFromError } = useToastCreator('toast-batch-ticket-basket-actions');

  const inputProps = {
    textValue: inputTextValue,
    textColor: inputTextColor,
  };

  const handleEditTag = (value: CustomerItemTagObject) => {
    setMode('edit');
    setValue('tagValue', value ? value.tagName : '');
    setItemEdit(value);
  };

  const handleReturnToAddMode = () => {
    setMode('add');
    setValue('tagValue', '');
  };

  const handleConfirmRemoveTag = async (id: string) => {
    show({ title: '', description: '' });
    setMode('add');
    setValue('tagValue', '');
    await removeTag(id);
    hide();
  };

  const handleLinkUnlinkTag = async (item: CustomerItemTagObject, isSelected: boolean) => {
    setLoading(true);
    isSelected
      ? await linkUnlinkTag({ ...item, customerId: itemId })
      : await addAndLinkTag({ ...item, customerId: itemId });
    setLoading(false);
  };

  const handleConfirmEditTag = async (data: CustomerItemTagObject) => {
    show({ title: '', description: '' });
    await updateTagLabel({ ...data, customerId: itemId });
    setValue('tagValue', '');
    setMode('add');
    setItemEdit({} as CustomerItemTagObject);
    hide();
  };

  const handleConfirmEdit = debounce((data: CustomerItemTagObject) => {
    confirm(confirmModalProps, () => handleConfirmEditTag(data));
  }, 400);

  const handleConfirmRemove = debounce(async (id: string) => {
    confirm({ ...confirmModalProps }, () => {
      handleConfirmRemoveTag(id);
    });
  }, 400);

  const handleCreateOrEditTag = async (data: CustomerItemTagObject) => {
    try {
      if (mode === 'edit') {
        return handleConfirmEdit(data);
      }

      show({ title: '', description: '' });

      setLoading(true);
      await addAndLinkTag({ ...data, customerId: itemId });
      setValue('tagValue', '');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
      setLoading(false);
    }
  };

  return {
    createNewTagIsValid,
    control,
    inputProps,
    errors,
    loading,
    itemEdit,
    mode,
    handleEditTag,
    handleConfirmRemove,
    handleReturnToAddMode,
    handleCreateOrEditTag,
    handleSubmit,
    handleLinkUnlinkTag,
  };
};
